import { type MouseEvent } from 'react';

// ** Next
import Link from 'next/link';
import { useRouter } from 'next/router';

// ** MUI
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';

// ** Components
import FaIconUvilo, { type IconPrefix } from '@erikdakoda/ui/components/FaIconUvilo';

// ** Theme Config
import themeConfig from '../../../../config';
import type { Settings } from '../../../../context/settingsContext';

// ** Types
import type { NavLink } from '../../../../types';
import { handleURLQueries } from '../../../../layouts/utils';

// ** Utils
import clsx from 'clsx';

type Props = {
  item: NavLink;
  settings: Settings;
  hasParent: boolean;
  /** Called when this item is selected so the popup menu can close. */
  onCloseMenu?: () => void;
};

/**
 * Horizontal nav link. Top-level items use ListItemButton (outside Menu);
 * nested items use MenuItem inside the parent Menu.
 */
export default function HorizontalNavLink(props: Props): React.JSX.Element {
  'use memo';

  const { item, settings, hasParent, onCloseMenu } = props;

  const router = useRouter();
  const { navSubItemIcon } = themeConfig.config;
  const { menuTextHidden } = settings;

  const icon = item.icon ? item.icon : navSubItemIcon;

  const isNavLinkActive = (): boolean => {
    if (router.pathname === item.path || handleURLQueries(router, item.path)) {
      return true;
    }
    return false;
  };

  const sharedClassName = clsx('horizontal-nav-link', {
    'top-nav-link': !hasParent,
    mj: true,
    'has-parent': hasParent,
    'Mui-selected': isNavLinkActive(),
  });

  const sharedSx = {
    ...(item.disabled ? { pointerEvents: 'none' as const } : { cursor: 'pointer' as const }),
    ...(!hasParent
      ? {
          px: 4.5,
        }
      : { px: 4 }),
  };

  const handleClick = (event: MouseEvent): void => {
    if (hasParent) {
      onCloseMenu?.();
    }
    if (item.path === undefined) {
      event.preventDefault();
      event.stopPropagation();
      item.onClick?.();
    }
  };

  const content = (
    <>
      <ListItemIcon sx={{ mr: hasParent ? 4 : menuTextHidden ? 0 : 2, minWidth: 'auto' }}>
        <FaIconUvilo icon={icon} prefix={item.prefix as IconPrefix} size="lg" />
      </ListItemIcon>
      {(hasParent || !menuTextHidden) && <ListItemText>{item.title}</ListItemText>}
      {item.badgeContent ? (
        <Chip
          label={item.badgeContent}
          color={item.badgeColor || 'primary'}
          sx={{
            height: 20,
            fontWeight: 500,
            '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' },
          }}
        />
      ) : null}
    </>
  );

  const nestedMenuItem = (
    <MenuItem
      component={Link}
      disabled={item.disabled}
      {...(item.disabled && { tabIndex: -1 })}
      className={sharedClassName}
      target={item.openInNewTab ? '_blank' : undefined}
      href={item.path === undefined ? '/' : `${item.path}`}
      onClick={handleClick}
      selected={isNavLinkActive()}
      sx={sharedSx}
    >
      {content}
    </MenuItem>
  );

  const topLevelItem = (
    <List
      className="horizontal-nav-link"
      component="div"
      sx={{ py: settings.skin === 'bordered' ? 2.625 : 2.75 }}
    >
      <ListItemButton
        component={Link}
        disabled={item.disabled}
        {...(item.disabled && { tabIndex: -1 })}
        className={sharedClassName}
        target={item.openInNewTab ? '_blank' : undefined}
        href={item.path === undefined ? '/' : `${item.path}`}
        onClick={handleClick}
        selected={isNavLinkActive()}
        sx={sharedSx}
      >
        {content}
      </ListItemButton>
    </List>
  );

  const navItem = hasParent ? nestedMenuItem : topLevelItem;

  if (menuTextHidden && !hasParent) {
    return (
      <Tooltip className="horizontal-nav-link" title={item.title}>
        {navItem}
      </Tooltip>
    );
  }
  return navItem;
}
